// ─── Main Renderer ────────────────────────────────────────────────────────────

import {
  TILE_SIZE,
  TileType,
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  DEPARTMENT_COLORS,
  type RoomData,
  type Interactable,
} from "./constants";
import type { Camera } from "./camera";
import type { SpriteSheet } from "./sprite-sheet";
import { getCachedSprite } from "./sprite-sheet";
import type { Character } from "./character";
import type { InteractionSystem } from "./interaction";
import type { SectionRenderer, WorkstationInfo } from "./section-renderer";
import type { GameView } from "@/stores/game-store";
import type { Pet } from "./pet";
import type { GeneratedAssets } from "./asset-generator";
import type { WildSprite } from "./wild-sprites";
import {
  hash2D,
  grassVariantKey,
  grassFlowerKey,
  neighborMask,
  isGrassLike,
  isLand,
  PATH_EDGE_KEYS,
  WATER_EDGE_KEYS,
  EDGE_N,
  EDGE_E,
  EDGE_S,
  EDGE_W,
} from "./autotile";
import {
  getWorldProps,
  drawWorldProp,
  propBaselineY,
  MAX_PROP_HEIGHT,
  type WorldProp,
} from "./render-props";
import {
  POSTFX_ENABLED,
  applyColorGrade,
  applyBloom,
  applyDepthHaze,
  applyVignette,
  AmbientParticles,
  type GradeKey,
  type ParticleKind,
} from "./post-fx";
import { getZoneByRoomId } from "@/data/zones";
import { dayNightCycle } from "./day-night";

// Suppress unused-import warnings — these stay for external API stability.
void CANVAS_WIDTH;
void CANVAS_HEIGHT;
void DEPARTMENT_COLORS;
type _InteractableRef = Interactable;

export interface GameRenderState {
  view: GameView;
  room: RoomData | null;
  player: Character;
  surggies: Character[];
  pets: Pet[];
  interactionSystem: InteractionSystem;
  sectionRenderer: SectionRenderer;
  workstations: WorkstationInfo[];
  frameCount: number;
  playerXp?: number;
  playerLevel?: number;
}

// ── Sprite key mapping ────────────────────────────────────────────────────────
// Centralised lookup from TileType → manifest sprite key. Keeps the renderer
// honest: if a key isn't in the manifest, getCachedSprite() returns null and we
// fall through to the subtle gray placeholder.

const TILE_SPRITE_KEY: Partial<Record<TileType, string>> = {
  [TileType.Sand]: "tile_sand",
  // tile_grass PNG has horizontal bands — use procedural generator instead
  [TileType.Boardwalk]: "tile_boardwalk",
  [TileType.Wall]: "tile_wall",
  [TileType.Pool]: "tile_pool",
  [TileType.Door]: "tile_door",
  [TileType.BuildingFloor]: "tile_floor",
  [TileType.BuildingExterior]: "tile_wall",
  [TileType.Path]: "tile_path",
  [TileType.FlowerPatch]: "tile_flowerPatch",
  [TileType.DarkGrass]: "tile_darkGrass",
  [TileType.TreeCanopy]: "tile_treeCanopy",
  [TileType.Rock]: "tile_rock",
};

// Character sheet conventions (CREDITS.md):
//   64×128 PNG, 4 cols (anim frames) × 4 rows (directions: down/left/right/up)
const CHAR_CELL_W = 16;
const CHAR_CELL_H = 32;
const CHAR_DIR_ROWS = 4; // down, left, right, up
const CHAR_ANIM_COLS = 4;

// Subtle placeholder colour — neutral gray, never bright.
const PLACEHOLDER_FILL = "rgba(60, 64, 72, 0.55)";
const PLACEHOLDER_STROKE = "rgba(140, 145, 155, 0.75)";

// PIXEL_STYLE.md palette colours used by procedural blends/overlays.
const MOSS = "#7a9a4a"; // foliage mid — path↔grass blend strips
const FOREST = "#5c7935"; // foliage shadow — tall-grass blades
const DEEP_FOREST = "#3d5224"; // deepest foliage — tall-grass blade shadows
const PALE_SKY = "#cfe7f5"; // water foam
const SHADOW_FILL = "rgba(26, 26, 26, 0.25)"; // ink @ 0.25 — character drop shadow

// ── Post-FX zone mapping ─────────────────────────────────────────────────────
// Derive a color-grade key + ambient-particle kind from the active room's zone.
// Each tuple is [GradeKey, ParticleKind]. Unknown rooms fall back to grass.
const ZONE_POSTFX: Record<string, [GradeKey, ParticleKind]> = {
  dev_dungeon: ["cave", "dust"],
  sales_floor: ["office", "dust"],
  finance_hub: ["office", "dust"],
  creative_lab: ["creative", "sparkle"],
  ops_center: ["ops", "ember"],
  default: ["grass", "pollen"], // wild_grass / overworld
};

function zonePostFx(roomId: string | undefined): [GradeKey, ParticleKind] {
  if (roomId && ZONE_POSTFX[roomId]) return ZONE_POSTFX[roomId];
  const zone = roomId ? getZoneByRoomId(roomId) : undefined;
  if (zone && ZONE_POSTFX[zone.roomId]) return ZONE_POSTFX[zone.roomId];
  return ZONE_POSTFX.default;
}

/**
 * Main renderer orchestrates all drawing: tile map, buildings/props,
 * characters, HUD overlays. Handles draw-order (ground -> objects ->
 * characters -> overlay).
 *
 * Sprites come from the manifest-loaded sprite cache via getCachedSprite().
 * When a sprite is missing, we fall back to the procedural asset-generator
 * output where available, then to a subtle gray-outline placeholder. We
 * never draw bright-coloured rectangles — those look amateur.
 */
export class Renderer {
  private sprites: SpriteSheet;
  private camera: Camera;
  private assets: GeneratedAssets | null = null;
  private levelUpFlashTimer = 0;
  private _lastXp: number | undefined;
  private _lastLevel: number | undefined;

  // ── Post-FX atmosphere state ──────────────────────────────────────────────
  // One reusable particle field (kind swapped in place on zone change — no
  // reallocation). _postFxLast tracks performance.now() for particle dt.
  private ambient: AmbientParticles | null = null;
  private _ambientKind: ParticleKind | null = null;
  private _postFxLast = 0;

  constructor(sprites: SpriteSheet, camera: Camera) {
    this.sprites = sprites;
    this.camera = camera;
  }

  setAssets(assets: GeneratedAssets): void {
    this.assets = assets;
  }

  /**
   * Render a full frame.
   */
  private xpBase = 100;
  private xpMultiplier = 1.5;

  private xpToNextLevel(level: number): number {
    return Math.floor(this.xpBase * Math.pow(level, this.xpMultiplier));
  }

  render(ctx: CanvasRenderingContext2D, state: GameRenderState): void {
    // Crisp pixel art — disable smoothing once per frame.
    ctx.imageSmoothingEnabled = false;

    this.levelUpFlashTimer = Math.max(0, this.levelUpFlashTimer - 1);
    if (state.playerLevel && state.playerXp !== undefined) {
      const currentXp = state.playerXp;
      const currentLevel = state.playerLevel;
      if (!this._lastXp || this._lastLevel !== currentLevel) {
        if (this._lastLevel && currentLevel > this._lastLevel) {
          this.levelUpFlashTimer = 120;
        }
        this._lastXp = currentXp;
        this._lastLevel = currentLevel;
      }
    }

    ctx.fillStyle = "#0A1628";
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.imageSmoothingEnabled = false;

    if (!state.room) return;

    // Apply camera transform
    this.camera.apply(ctx);

    // Layer 1: Ground tiles
    this.drawTileMap(ctx, state.room, state.frameCount);

    // Layer 2: Props / building decorations
    this.drawProps(ctx, state.room);

    // Layer 3: Interactable overlays (building labels, section markers)
    this.drawInteractableOverlays(ctx, state.room);

    // Layer 4: World props + characters + pets — merged by baseline Y so a
    // tree behind the player draws first and one in front draws over.
    const allCharacters = [state.player, ...state.surggies];
    allCharacters.sort((a, b) => a.position.y - b.position.y);

    // Merge pets into Y-sorted draw order
    const pets = [...state.pets].sort((a, b) => a.sortY - b.sortY);
    const props = getWorldProps(); // pre-sorted by ty
    const propBounds = this.camera.getVisibleBounds();
    this.propCursor = 0;
    let pi = 0;
    for (const character of allCharacters) {
      while (pi < pets.length && pets[pi].sortY < character.position.y) {
        this.drawPropsUpTo(ctx, props, pets[pi].sortY, propBounds);
        pets[pi].draw(ctx, this.camera);
        pi++;
      }
      this.drawPropsUpTo(ctx, props, character.position.y, propBounds);
      this.drawCharacter(ctx, character);
    }
    while (pi < pets.length) {
      this.drawPropsUpTo(ctx, props, pets[pi].sortY, propBounds);
      pets[pi].draw(ctx, this.camera);
      pi++;
    }
    this.drawPropsUpTo(ctx, props, Infinity, propBounds);

    // Layer 4.5: Tall-grass top tufts — re-drawn over entities so anyone
    // walking through tall grass appears half-hidden.
    this.drawTallGrassOverlay(ctx, state.room);

    // Layer 5: Interaction prompt (drawn in world space)
    state.interactionSystem.drawPrompt(ctx);

    // Restore camera transform
    this.camera.restore(ctx);

    // Layer 5.5: Subtle vignette (screen space, under the HUD)
    this.drawVignette(ctx);

    // Layer 5.6: Atmosphere pass — per-zone color grade, soft bloom, depth
    // haze, ambient particles, and a time-of-day grade. All gated behind
    // POSTFX_ENABLED, cached buffers, pooled particles. Runs after the vignette
    // and before the day-night overlay (which the caller draws next).
    this.drawAtmosphere(ctx, state);

    // Layer 6: HUD overlay (drawn in screen space)
    this.drawHUD(ctx, state);
  }

  // ─── World props (depth-merged with entities) ──────────────────────────────
  // Cursor into the ty-sorted prop list, reset each frame. Props are compared
  // against character top-Y, so a prop's pseudo-Y is its baseline minus the
  // character height — same convention the pet merge already uses.
  private propCursor = 0;

  private drawPropsUpTo(
    ctx: CanvasRenderingContext2D,
    props: readonly WorldProp[],
    limitY: number,
    bounds: { x: number; y: number; width: number; height: number },
  ): void {
    while (this.propCursor < props.length) {
      const prop = props[this.propCursor];
      const baseline = propBaselineY(prop);
      if (baseline - CHAR_CELL_H >= limitY) break;
      this.propCursor++;
      // Viewport cull — sprites extend up from the baseline.
      if (baseline < bounds.y) continue;
      if (baseline - MAX_PROP_HEIGHT > bounds.y + bounds.height) continue;
      const px = prop.tx * TILE_SIZE;
      if (px + MAX_PROP_HEIGHT < bounds.x || px - MAX_PROP_HEIGHT > bounds.x + bounds.width) {
        continue;
      }
      drawWorldProp(ctx, prop); // missing sprite key → skips silently
    }
  }

  /**
   * Draw a single character using its sprite sheet from the manifest cache.
   * Falls back to the legacy SpriteSheet class draw (which itself handles
   * procedural placeholders) when the manifest sprite is not yet loaded.
   */
  private drawCharacter(ctx: CanvasRenderingContext2D, character: Character): void {
    const key = `char_${character.spriteKey}`;
    const img = getCachedSprite(key);

    // Integer pixel positions — avoid sub-pixel blur on pixel art.
    const dx = Math.round(character.position.x);
    const dy = Math.round(character.position.y);

    // Soft drop shadow under the feet, drawn before the sprite.
    this.drawShadowEllipse(ctx, dx + CHAR_CELL_W / 2, dy + CHAR_CELL_H - 2);

    if (img) {
      // Row = direction (down=0, left=1, right=2, up=3); Col = anim frame.
      const dir = Math.max(0, Math.min(CHAR_DIR_ROWS - 1, character.direction as number));
      const frame = Math.max(0, Math.min(CHAR_ANIM_COLS - 1, character.animFrame | 0));

      ctx.drawImage(
        img,
        frame * CHAR_CELL_W,
        dir * CHAR_CELL_H,
        CHAR_CELL_W,
        CHAR_CELL_H,
        dx,
        dy,
        CHAR_CELL_W,
        CHAR_CELL_H,
      );
      return;
    }

    // No manifest sprite yet — defer to the existing Character.draw path so
    // any procedural fallback or alternate sheet logic in SpriteSheet still
    // runs. If that path also fails, draw the subtle gray placeholder.
    if (typeof character.draw === "function") {
      character.draw(ctx, this.camera, this.sprites);
      return;
    }

    this.drawPlaceholderBox(ctx, dx, dy, CHAR_CELL_W, CHAR_CELL_H);
  }

  /**
   * Subtle 1px-outline neutral-gray placeholder. Never bright — bright
   * coloured rectangles read as amateur.
   */
  private drawPlaceholderBox(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
  ): void {
    ctx.save();
    ctx.fillStyle = PLACEHOLDER_FILL;
    ctx.fillRect(x, y, w, h);
    ctx.lineWidth = 1;
    ctx.strokeStyle = PLACEHOLDER_STROKE;
    // Offset by 0.5 for crisp 1px stroke on integer-aligned rects.
    ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    ctx.restore();
  }

  /**
   * Soft drop shadow under a character: ink ellipse at 0.25 alpha, ~10x4px
   * at 1x scale, centred at (cx, cy) on the ground.
   */
  private drawShadowEllipse(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
  ): void {
    ctx.fillStyle = SHADOW_FILL;
    ctx.beginPath();
    ctx.ellipse(cx, cy, 5, 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Procedural fallback when an autotile edge sprite is missing: a 2px blend
   * strip in a palette colour on each "outside" side, plus a couple of
   * hash-dithered pixels so the seam doesn't read as a ruler line. The world
   * must look better even before the edge PNGs land.
   */
  private drawBlendStrips(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    mask: number,
    color: string,
    col: number,
    row: number,
  ): void {
    ctx.fillStyle = color;
    const h = hash2D(col, row);
    if (mask & EDGE_N) {
      ctx.fillRect(x, y, TILE_SIZE, 2);
      ctx.fillRect(x + (h & 7), y + 2, 2, 1);
      ctx.fillRect(x + 8 + ((h >>> 3) % 6), y + 2, 2, 1);
    }
    if (mask & EDGE_S) {
      ctx.fillRect(x, y + TILE_SIZE - 2, TILE_SIZE, 2);
      ctx.fillRect(x + ((h >>> 6) & 7), y + TILE_SIZE - 3, 2, 1);
      ctx.fillRect(x + 8 + ((h >>> 9) % 6), y + TILE_SIZE - 3, 2, 1);
    }
    if (mask & EDGE_W) {
      ctx.fillRect(x, y, 2, TILE_SIZE);
      ctx.fillRect(x + 2, y + ((h >>> 12) & 7), 1, 2);
      ctx.fillRect(x + 2, y + 8 + ((h >>> 15) % 6), 1, 2);
    }
    if (mask & EDGE_E) {
      ctx.fillRect(x + TILE_SIZE - 2, y, 2, TILE_SIZE);
      ctx.fillRect(x + TILE_SIZE - 3, y + ((h >>> 18) & 7), 1, 2);
      ctx.fillRect(x + TILE_SIZE - 3, y + 8 + ((h >>> 21) % 6), 1, 2);
    }
  }

  // ─── Tall grass top overlay ─────────────────────────────────────────────────
  // Re-drawn over entities so the player/followers walking through tall grass
  // appear half-hidden. Prefers the tile_tallgrass_top sprite; falls back to
  // procedural blade tufts pre-rendered once into 4 offscreen 16x16 canvases
  // (no per-frame fillRect churn).
  private tallGrassTufts: HTMLCanvasElement[] | null = null;

  private getTallGrassTufts(): HTMLCanvasElement[] | null {
    if (this.tallGrassTufts) return this.tallGrassTufts;
    if (typeof document === "undefined") return null;
    const variants: HTMLCanvasElement[] = [];
    for (let v = 0; v < 4; v++) {
      const canvas = document.createElement("canvas");
      canvas.width = TILE_SIZE;
      canvas.height = TILE_SIZE;
      const c = canvas.getContext("2d");
      if (!c) return null;
      // 6 blades per tile: 2px-wide verticals anchored at the tile bottom,
      // forest body with a deep-forest shadow column on the right (top-left
      // light) — deterministic per variant.
      for (let b = 0; b < 6; b++) {
        const hb = hash2D(v * 31 + b, b * 17 + v);
        const bx = (b * 3 + (hb & 1)) % (TILE_SIZE - 2);
        const bh = 6 + (hb % 5); // 6..10px tall
        c.fillStyle = FOREST;
        c.fillRect(bx, TILE_SIZE - bh, 1, bh);
        c.fillStyle = DEEP_FOREST;
        c.fillRect(bx + 1, TILE_SIZE - bh + 1, 1, bh - 1);
      }
      variants.push(canvas);
    }
    this.tallGrassTufts = variants;
    return variants;
  }

  private drawTallGrassOverlay(
    ctx: CanvasRenderingContext2D,
    room: RoomData,
  ): void {
    const bounds = this.camera.getVisibleBounds();
    const startCol = Math.max(0, Math.floor(bounds.x / TILE_SIZE));
    const endCol = Math.min(room.width - 1, Math.ceil((bounds.x + bounds.width) / TILE_SIZE));
    const startRow = Math.max(0, Math.floor(bounds.y / TILE_SIZE));
    const endRow = Math.min(room.height - 1, Math.ceil((bounds.y + bounds.height) / TILE_SIZE));

    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        if ((room.tileMap[row][col] as TileType) !== TileType.DarkGrass) continue;
        const x = col * TILE_SIZE;
        const y = row * TILE_SIZE;
        if (this.tryDrawSprite(ctx, "tile_tallgrass_top", x, y)) continue;
        const tufts = this.getTallGrassTufts();
        if (tufts) ctx.drawImage(tufts[hash2D(col, row) & 3], x, y);
      }
    }
  }

  // ─── Vignette ───────────────────────────────────────────────────────────────
  // Very subtle radial darkening toward the corners, drawn in screen space
  // after entities and before the HUD. The gradient is rebuilt only when the
  // canvas size changes.
  private vignette: { w: number; h: number; gradient: CanvasGradient } | null = null;

  private drawVignette(ctx: CanvasRenderingContext2D): void {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    // When the atmosphere pass is on, use the shared cinematic vignette (cached
    // in post-fx.ts) so we draw one, slightly stronger vignette rather than two.
    if (POSTFX_ENABLED) {
      applyVignette(ctx, w, h);
      return;
    }
    if (!this.vignette || this.vignette.w !== w || this.vignette.h !== h) {
      const r = Math.hypot(w, h) / 2;
      const gradient = ctx.createRadialGradient(w / 2, h / 2, r * 0.55, w / 2, h / 2, r);
      gradient.addColorStop(0, "rgba(26, 26, 26, 0)");
      gradient.addColorStop(1, "rgba(26, 26, 26, 0.12)");
      this.vignette = { w, h, gradient };
    }
    ctx.fillStyle = this.vignette.gradient;
    ctx.fillRect(0, 0, w, h);
  }

  // ─── Atmosphere pass (post-FX) ───────────────────────────────────────────────
  // Per-zone color grade + soft bloom + depth haze + ambient particles + a
  // time-of-day grade, layered over the crisp scene. Order: grade → bloom →
  // haze → particles. Bloom samples the already-graded frame so highlights
  // glow in the mood color. Everything is cached/pooled for 60fps.
  private drawAtmosphere(
    ctx: CanvasRenderingContext2D,
    state: GameRenderState,
  ): void {
    if (!POSTFX_ENABLED) return;
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;

    const [gradeKey, particleKind] = zonePostFx(state.room?.id);

    // 1) Per-zone color grade (subtle multiply + screen).
    applyColorGrade(ctx, w, h, gradeKey);

    // 2) Time-of-day grade routed through the same path so dawn/dusk warm and
    //    night cools the frame as light, not a flat overlay. 'day' is a near
    //    no-op; the existing day-night overlay still handles darkness.
    applyColorGrade(ctx, w, h, dayNightCycle.phase as GradeKey);

    // 3) Soft bloom — quarter-res, additive, samples the graded frame.
    applyBloom(ctx, w, h);

    // 4) Depth haze — soft horizon gradient (palette pale-sky).
    applyDepthHaze(ctx, w, h);

    // 5) Ambient particle field — pooled, kind swapped in place on zone change.
    if (!this.ambient) {
      this.ambient = new AmbientParticles(particleKind);
      this._ambientKind = particleKind;
    } else if (this._ambientKind !== particleKind) {
      this.ambient.setKind(particleKind);
      this._ambientKind = particleKind;
    }
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    let dt = this._postFxLast ? now - this._postFxLast : 16;
    this._postFxLast = now;
    if (dt < 0 || dt > 100) dt = 16; // clamp tab-switch spikes
    const bounds = { x: 0, y: 0, width: w, height: h };
    this.ambient.update(dt, bounds);
    this.ambient.draw(ctx, bounds);
  }

  /**
   * Try to draw a sprite from the manifest cache at (x,y) with the tile size.
   * Returns true when something was drawn.
   */
  private tryDrawSprite(
    ctx: CanvasRenderingContext2D,
    key: string,
    x: number,
    y: number,
    w: number = TILE_SIZE,
    h: number = TILE_SIZE,
  ): boolean {
    const img = getCachedSprite(key);
    if (!img) return false;
    ctx.drawImage(img, Math.round(x), Math.round(y), w, h);
    return true;
  }

  /**
   * Draw the tile map. Prefers manifest sprites, then procedural assets,
   * then subtle gray placeholder.
   */
  private drawTileMap(
    ctx: CanvasRenderingContext2D,
    room: RoomData,
    frameCount: number,
  ): void {
    const bounds = this.camera.getVisibleBounds();

    const startCol = Math.max(0, Math.floor(bounds.x / TILE_SIZE));
    const endCol = Math.min(room.width - 1, Math.ceil((bounds.x + bounds.width) / TILE_SIZE));
    const startRow = Math.max(0, Math.floor(bounds.y / TILE_SIZE));
    const endRow = Math.min(room.height - 1, Math.ceil((bounds.y + bounds.height) / TILE_SIZE));

    // Ocean animation: cycle through 8 pre-generated frames
    const oceanFrame = Math.floor(frameCount / 4) % 8;

    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const tile = room.tileMap[row][col] as TileType;
        const x = col * TILE_SIZE;
        const y = row * TILE_SIZE;

        // 1) Animated ocean — autotiled foam edges where water meets land.
        if (tile === TileType.Ocean || tile === TileType.WaveAnimated) {
          const mask = neighborMask(room, col, row, isLand);
          if (mask !== 0) {
            const edgeKey = WATER_EDGE_KEYS[mask];
            if (edgeKey && this.tryDrawSprite(ctx, edgeKey, x, y)) continue;
          }
          // Base water: numbered animation frame, then procedural, then placeholder.
          if (!this.tryDrawSprite(ctx, `tile_ocean_${oceanFrame}`, x, y)) {
            if (this.assets?.tiles.ocean?.[oceanFrame]) {
              ctx.drawImage(
                this.assets.tiles.ocean[oceanFrame] as CanvasImageSource,
                x, y, TILE_SIZE, TILE_SIZE,
              );
            } else {
              this.drawPlaceholderBox(ctx, x, y, TILE_SIZE, TILE_SIZE);
            }
          }
          // Edge sprite missing — procedural pale-sky foam strips instead.
          if (mask !== 0) this.drawBlendStrips(ctx, x, y, mask, PALE_SKY, col, row);
          continue;
        }

        // 1b) Grass — deterministic texture variants + ~4% flower accents.
        if (tile === TileType.Grass) {
          const flowerKey = grassFlowerKey(col, row);
          if (flowerKey && this.tryDrawSprite(ctx, flowerKey, x, y)) continue;
          if (this.tryDrawSprite(ctx, grassVariantKey(col, row), x, y)) continue;
          // Variants not loaded yet — fall through to the procedural grass below.
        }

        // 1c) Tall grass — base half here; the top tuft is re-drawn over
        // entities in drawTallGrassOverlay() so characters sit half-hidden.
        if (tile === TileType.DarkGrass) {
          if (this.tryDrawSprite(ctx, "tile_tallgrass_base", x, y)) continue;
          // Fall through to tile_darkGrass / procedural below.
        }

        // 1d) Path — autotiled grass-blend edges and corners.
        if (tile === TileType.Path) {
          const mask = neighborMask(room, col, row, isGrassLike);
          if (mask !== 0) {
            const edgeKey = PATH_EDGE_KEYS[mask];
            if (edgeKey && this.tryDrawSprite(ctx, edgeKey, x, y)) continue;
            // Edge sprite missing — base path + procedural moss blend strips.
            if (!this.tryDrawSprite(ctx, "tile_path", x, y)) {
              if (this.assets?.tiles.path) {
                ctx.drawImage(
                  this.assets.tiles.path as CanvasImageSource,
                  x, y, TILE_SIZE, TILE_SIZE,
                );
              } else {
                this.drawPlaceholderBox(ctx, x, y, TILE_SIZE, TILE_SIZE);
              }
            }
            this.drawBlendStrips(ctx, x, y, mask, MOSS, col, row);
            continue;
          }
          // No grass neighbours — plain path via the normal cascade below.
        }

        // 2) Static tiles — try manifest key.
        const key = TILE_SPRITE_KEY[tile];
        if (key && this.tryDrawSprite(ctx, key, x, y)) continue;

        // 3) Procedural fallback from asset-generator.
        if (this.assets) {
          const t = this.assets.tiles;
          let drew = true;
          switch (tile) {
            case TileType.Sand:
              ctx.drawImage(t.sand as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            case TileType.Boardwalk:
              ctx.drawImage(t.boardwalk as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            case TileType.Grass:
              ctx.drawImage(t.grass as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            case TileType.Wall:
            case TileType.BuildingExterior:
              ctx.drawImage(t.wall as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            case TileType.Pool:
              ctx.drawImage(t.pool as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            case TileType.Path:
              ctx.drawImage(t.path as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            case TileType.FlowerPatch:
              ctx.drawImage(t.flowerPatch as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            case TileType.DarkGrass:
              ctx.drawImage(t.darkGrass as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            case TileType.TreeCanopy:
              ctx.drawImage(t.treeCanopy as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            case TileType.Rock:
              // Draw grass base first, then rock on top
              ctx.drawImage(t.grass as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              ctx.drawImage(t.rock as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
              break;
            default:
              drew = false;
          }
          if (drew) continue;
        }

        // 4) Last resort — subtle gray placeholder. Empty tiles get nothing.
        if (tile !== TileType.Empty) {
          this.drawPlaceholderBox(ctx, x, y, TILE_SIZE, TILE_SIZE);
        }
      }
    }
  }

  /**
   * Draw props (palm trees, tiki bar, etc.). Prefers manifest sprites,
   * then procedural assets, then subtle gray placeholder.
   */
  private drawProps(ctx: CanvasRenderingContext2D, room: RoomData): void {
    for (let row = 0; row < room.height; row++) {
      for (let col = 0; col < room.width; col++) {
        const tile = room.tileMap[row][col] as TileType;

        if (tile === TileType.PalmTrunk) {
          // 32×48 sprite — centred on a 16px tile, bottom-aligned with tile bottom.
          const px = col * TILE_SIZE - 8;
          const py = row * TILE_SIZE - 32;
          if (this.tryDrawSprite(ctx, "prop_palmTree", px, py, 32, 48)) continue;
          if (this.assets?.props.palmTree) {
            ctx.drawImage(
              this.assets.props.palmTree as CanvasImageSource,
              px, py, 32, 48,
            );
            continue;
          }
          this.drawPlaceholderBox(ctx, px, py, 32, 48);
        }

        // PalmLeaves: a palm sprite covers this tile when present, so we
        // intentionally draw nothing here. (No bright leaf-circle fallback.)

        if (tile === TileType.TikiBar) {
          // 48×32 sprite — centred on a 16px tile.
          const tx = col * TILE_SIZE - 16;
          const ty = row * TILE_SIZE;
          if (this.tryDrawSprite(ctx, "prop_tikiBar", tx, ty, 48, 32)) continue;
          if (this.assets?.props.tikiBar) {
            ctx.drawImage(
              this.assets.props.tikiBar as CanvasImageSource,
              tx, ty, 48, 32,
            );
            continue;
          }
          this.drawPlaceholderBox(ctx, tx, ty, 48, 32);
        }
      }
    }
  }

  /**
   * Draw labels/markers on interactable objects. For buildings with a
   * matching `bldg_*` manifest sprite, render the sprite as the building
   * exterior.
   */
  private drawInteractableOverlays(
    ctx: CanvasRenderingContext2D,
    room: RoomData,
  ): void {
    for (const interactable of room.interactables) {
      if (interactable.type === "building") {
        const slug =
          (interactable.data?.slug as string | undefined) ||
          (interactable.data?.type as string | undefined) ||
          (interactable.data?.name as string | undefined)?.toLowerCase().replace(/\s+hq\s*/gi, "").trim().replace(/\s+/g, "_");

        // Use full building plot dimensions when stored, else fall back to door bounds.
        const bx = Math.round((interactable.data?.plotX as number | undefined) ?? interactable.position.x);
        const by = Math.round((interactable.data?.plotY as number | undefined) ?? interactable.position.y);
        const bw = (interactable.data?.plotW as number | undefined) ?? interactable.width;
        const bh = (interactable.data?.plotH as number | undefined) ?? interactable.height;

        if (slug) {
          if (!this.tryDrawSprite(ctx, `bldg_${slug}`, bx, by, bw, bh)) {
            const proc = this.assets?.buildings[slug];
            if (proc) {
              ctx.drawImage(proc as CanvasImageSource, bx, by, bw, bh);
            }
          }
        }

      }

      if (interactable.type === "section" && interactable.data?.name) {
        // Section area outline
        ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.strokeRect(
          interactable.position.x,
          interactable.position.y,
          interactable.width,
          interactable.height,
        );
        ctx.setLineDash([]);

        // Section name
        const name = interactable.data.name as string;
        ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
        ctx.font = "8px monospace";
        ctx.textAlign = "center";
        ctx.fillText(
          name,
          interactable.position.x + interactable.width / 2,
          interactable.position.y + interactable.height / 2,
        );
      }
    }
  }

  /**
   * Draw HUD overlay in screen space.
   */
  private drawHUD(
    ctx: CanvasRenderingContext2D,
    state: GameRenderState,
  ): void {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;

    // Building name labels — screen-space so they clear the HUD bar.
    if (state.room && state.view === "overworld") {
      const HUD_SAFE_Y = 68;
      const zoom = this.camera.zoom;
      for (const it of state.room.interactables) {
        if (it.type !== "building" || !it.data?.name) continue;
        const bx = (it.data?.plotX as number | undefined) ?? it.position.x;
        const bw2 = (it.data?.plotW as number | undefined) ?? it.width;
        const by2 = (it.data?.plotY as number | undefined) ?? it.position.y;
        const screenCx = (bx + bw2 / 2 - this.camera.x) * zoom;
        const screenTop = (by2 - this.camera.y) * zoom;
        if (screenCx < -80 || screenCx > w + 80) continue; // off-screen horizontally

        const labelY = Math.max(HUD_SAFE_Y, screenTop - 4);
        const name = it.data.name as string;
        ctx.font = "bold 9px monospace";
        const tw = ctx.measureText(name).width + 14;
        ctx.fillStyle = "rgba(0,0,0,0.75)";
        ctx.beginPath();
        ctx.roundRect(screenCx - tw / 2, labelY - 8, tw, 16, 4);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.25)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = "#FFFFFF";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(name, screenCx, labelY);
      }
    }

    // Room name (top center)
    if (state.room) {
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
      ctx.beginPath();
      ctx.roundRect(w / 2 - 80, 8, 160, 24, 6);
      ctx.fill();

      ctx.fillStyle = "#F4D03F";
      ctx.font = "bold 12px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(state.room.name, w / 2, 20);
    }

    // View indicator (top left)
    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    ctx.beginPath();
    ctx.roundRect(8, 8, 120, 20, 4);
    ctx.fill();

    const viewLabel =
      state.view === "overworld"
        ? "🌴 OVERWORLD"
        : state.view === "department"
          ? "🏢 DEPARTMENT"
          : "⚙️ SECTION";
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "10px monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(viewLabel, 14, 18);

    // Mini coordinates (bottom left)
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.beginPath();
    ctx.roundRect(8, h - 28, 120, 20, 4);
    ctx.fill();

    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "9px monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText(
      `📍 ${Math.round(state.player.position.x)} , ${Math.round(state.player.position.y)}`,
      12,
      h - 12,
    );

    // Interaction hint (bottom center) — styled prompt
    // Show when there's a nearby interactable
    const nearestTarget = state.interactionSystem.getNearestTarget?.();
    if (nearestTarget) {
      const promptText = "E — Interact";
      ctx.fillStyle = "rgba(0,0,0,0.8)";
      const pw = ctx.measureText(promptText).width + 24;
      ctx.beginPath();
      ctx.roundRect(w / 2 - pw / 2, h - 44, pw, 22, 6);
      ctx.fill();
      ctx.strokeStyle = "rgba(255,107,157,0.5)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#FF6B9D";
      ctx.font = "bold 11px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(promptText, w / 2, h - 33);
    }

    // Surggie count (top right)
    const surggieCount = state.surggies.length;
    if (surggieCount > 0) {
      ctx.fillStyle = "rgba(0,0,0,0.7)";
      ctx.beginPath();
      ctx.roundRect(w - 100, 8, 92, 20, 4);
      ctx.fill();
      ctx.fillStyle = "#00D4FF";
      ctx.font = "10px monospace";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(`🤖 ${surggieCount} Surggies`, w - 12, 18);
    }

    // Pet indicator (top right, below surggie count)
    if (state.pets.length > 0) {
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.beginPath();
      ctx.roundRect(w - 100, 32, 92, 18, 4);
      ctx.fill();
      ctx.fillStyle = "#FFD700";
      ctx.font = "9px monospace";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(`🐕 ${state.pets.length} Pets`, w - 12, 41);
    }

    // Player XP bar (bottom left, below coordinates)
    if (state.playerLevel !== undefined && state.playerXp !== undefined) {
      const level = state.playerLevel;
      const xp = state.playerXp;
      const xpNeeded = this.xpToNextLevel(level);
      const xpPercent = Math.min(1, xp / xpNeeded);

      ctx.fillStyle = "rgba(0,0,0,0.7)";
      ctx.beginPath();
      ctx.roundRect(8, h - 52, 160, 20, 4);
      ctx.fill();

      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.font = "8px monospace";
      ctx.textAlign = "left";
      ctx.textBaseline = "bottom";
      ctx.fillText(`Player Lv.${level}`, 12, h - 34);

      // XP bar background
      ctx.fillStyle = "rgba(255,255,255,0.15)";
      ctx.beginPath();
      ctx.roundRect(12, h - 24, 140, 8, 3);
      ctx.fill();

      // XP bar fill
      const barColor = xpPercent > 0.8 ? "#FFD700" : "#51CF66";
      ctx.fillStyle = barColor;
      ctx.beginPath();
      ctx.roundRect(12, h - 24, Math.floor(140 * xpPercent), 8, 3);
      ctx.fill();

      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.font = "7px monospace";
      ctx.textAlign = "right";
      ctx.fillText(`${xp}/${xpNeeded} XP`, 150, h - 17);

      // Level up flash
      if (this.levelUpFlashTimer > 0) {
        ctx.fillStyle = `rgba(255,215,0,${Math.min(1, this.levelUpFlashTimer / 30)})`;
        ctx.font = "bold 14px monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("🎉 Level Up!", w / 2, h / 2 - 80);
      }
    }
  }

  // ─── Wild sprites ──────────────────────────────────────────────────────────
  /**
   * Draw all visible wild Surggies to the world canvas. Applies (and
   * restores) the camera transform itself, so it can be called after the
   * main render() pass has restored the context.
   *
   * Each sprite is rendered from its species sprite sheet at
   * `char_<speciesId>` — same 64x128 (4 cols x 4 rows) layout as player
   * characters. Row = facing, column = animFrame % 4. Falls back to the
   * subtle gray placeholder when the sheet has not loaded yet.
   */
  drawWildSprites(
    ctx: CanvasRenderingContext2D,
    sprites: readonly WildSprite[],
  ): void {
    this.camera.apply(ctx);
    ctx.imageSmoothingEnabled = false;

    for (const sprite of sprites) {
      const key = `char_${sprite.speciesId}`;
      const img = getCachedSprite(key);

      const dx = Math.round(sprite.x * TILE_SIZE);
      const dy = Math.round(sprite.y * TILE_SIZE);

      const dirRow =
        sprite.facing === "down"
          ? 0
          : sprite.facing === "left"
            ? 1
            : sprite.facing === "right"
              ? 2
              : 3;
      const frameCol = ((sprite.animFrame | 0) % CHAR_ANIM_COLS + CHAR_ANIM_COLS) % CHAR_ANIM_COLS;

      // Soft drop shadow under the feet, drawn before the sprite.
      this.drawShadowEllipse(ctx, dx + CHAR_CELL_W / 2, dy + CHAR_CELL_H - 2);

      if (img) {
        ctx.drawImage(
          img,
          frameCol * CHAR_CELL_W,
          dirRow * CHAR_CELL_H,
          CHAR_CELL_W,
          CHAR_CELL_H,
          dx,
          dy,
          CHAR_CELL_W,
          CHAR_CELL_H,
        );
      } else {
        this.drawPlaceholderBox(ctx, dx, dy, CHAR_CELL_W, CHAR_CELL_H);
      }
    }
    this.camera.restore(ctx);
  }

  // ─── Day-night overlay ─────────────────────────────────────────────────────
  /**
   * Fill the entire canvas with a tinted overlay (rgba string from
   * dayNightCycle.overlayColor). Skipped when the overlay alpha is below a
   * small threshold so full daylight stays a no-op. Restores the previous
   * composite mode.
   */
  drawDayNightOverlay(
    ctx: CanvasRenderingContext2D,
    overlayColor: string,
  ): void {

    // Parse trailing alpha from an rgba(...) string. If alpha is essentially
    // 0 (daylight), skip — keeps the hot path free of full-canvas fills.
    const alpha = parseRgbaAlpha(overlayColor);
    if (alpha <= 0.01) return;

    const prevComposite = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = overlayColor;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.globalCompositeOperation = prevComposite;
  }

  // ─── Trainer "!" bubble ────────────────────────────────────────────────────
  /**
   * Pixel-art "!" speech bubble above a trainer. Drawn at world coords
   * (trainerX, trainerY) — caller is responsible for camera transform.
   * 16x16 cream bubble with 2px ink border, centred horizontally, 12px above
   * the trainer's head.
   */
  drawTrainerExclamation(trainerX: number, trainerY: number): void {
    const ctx = this.lastCtx;
    if (!ctx) return;

    const size = 16;
    const x = Math.round(trainerX - size / 2);
    const y = Math.round(trainerY - 12 - size);

    ctx.save();
    ctx.imageSmoothingEnabled = false;

    // 2px ink border (drawn as outer + inner rect to stay on the pixel grid).
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(x, y, size, size);
    // Cream fill, inset 2px on all sides.
    ctx.fillStyle = "#f5ecd6";
    ctx.fillRect(x + 2, y + 2, size - 4, size - 4);

    // Pixel "!" in ink, centred. Stem: 2x6 block; dot: 2x2 block below.
    ctx.fillStyle = "#1a1a1a";
    const cx = x + Math.floor(size / 2) - 1; // 2px wide → start one left of centre
    ctx.fillRect(cx, y + 4, 2, 6);
    ctx.fillRect(cx, y + 11, 2, 2);

    ctx.restore();
  }

  // ─── Internal: track ctx for the canvas-bound helpers above ────────────────
  // Methods added after the original render() rely on the same ctx the caller
  // is drawing into. We expose a setter so external systems can bind the
  // active canvas context before calling drawWildSprites / drawDayNightOverlay
  // / drawTrainerExclamation outside of the main render() entry point.
  private lastCtx: CanvasRenderingContext2D | null = null;

  bindContext(ctx: CanvasRenderingContext2D): void {
    this.lastCtx = ctx;
  }
}

// Parse the trailing alpha from an "rgba(r, g, b, a)" string. Returns 1 when
// no alpha is present (rgb() form) and 0 when the string cannot be parsed.
function parseRgbaAlpha(rgba: string): number {
  const m = /rgba?\s*\(\s*[^,]+,\s*[^,]+,\s*[^,]+(?:,\s*([0-9]*\.?[0-9]+))?\s*\)/i.exec(rgba);
  if (!m) return 0;
  if (m[1] === undefined) return 1;
  const a = parseFloat(m[1]);
  return Number.isFinite(a) ? a : 0;
}
